import type { fabric } from 'fabric';

export type CartoonStyle = 'comic' | 'cartoon';

export interface CartoonOptions {
  /** comic = stronger ink outlines + flatter colors; cartoon = softer cel look */
  style?: CartoonStyle;
  /** Max edge length while processing (keeps large photos responsive). */
  maxEdge?: number;
}

function clampByte(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v | 0;
}

function posterizeChannel(value: number, levels: number): number {
  const steps = Math.max(2, levels) - 1;
  return clampByte(Math.round((value / 255) * steps) * (255 / steps));
}

/** Simple box blur (odd radius) on an RGBA buffer — used to soften before quantize. */
function boxBlur(
  src: Uint8ClampedArray,
  w: number,
  h: number,
  radius: number
): Uint8ClampedArray {
  const r = Math.max(1, Math.round(radius));
  const out = new Uint8ClampedArray(src.length);
  const tmp = new Uint8ClampedArray(src.length);
  const span = r * 2 + 1;

  // Horizontal
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let sr = 0;
      let sg = 0;
      let sb = 0;
      let sa = 0;
      let n = 0;
      for (let k = -r; k <= r; k++) {
        const xx = Math.min(w - 1, Math.max(0, x + k));
        const i = (y * w + xx) * 4;
        sr += src[i];
        sg += src[i + 1];
        sb += src[i + 2];
        sa += src[i + 3];
        n++;
      }
      const o = (y * w + x) * 4;
      tmp[o] = sr / n;
      tmp[o + 1] = sg / n;
      tmp[o + 2] = sb / n;
      tmp[o + 3] = sa / n;
    }
  }

  // Vertical
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let sr = 0;
      let sg = 0;
      let sb = 0;
      let sa = 0;
      let n = 0;
      for (let k = -r; k <= r; k++) {
        const yy = Math.min(h - 1, Math.max(0, y + k));
        const i = (yy * w + x) * 4;
        sr += tmp[i];
        sg += tmp[i + 1];
        sb += tmp[i + 2];
        sa += tmp[i + 3];
        n++;
      }
      const o = (y * w + x) * 4;
      out[o] = sr / (n || span);
      out[o + 1] = sg / (n || span);
      out[o + 2] = sb / (n || span);
      out[o + 3] = sa / (n || span);
    }
  }
  return out;
}

function luminance(r: number, g: number, b: number): number {
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

/**
 * Comic / cartoon stylization for an image element.
 * Returns a PNG data URL (preserves transparency).
 */
export function renderComicDataUrl(
  image: HTMLImageElement | HTMLCanvasElement,
  options: CartoonOptions = {}
): string {
  const style = options.style || 'comic';
  const maxEdge = options.maxEdge ?? 1400;

  const srcW =
    'naturalWidth' in image ? image.naturalWidth || image.width : image.width;
  const srcH =
    'naturalHeight' in image ? image.naturalHeight || image.height : image.height;
  if (!srcW || !srcH) throw new Error('Image has no dimensions');

  const scale = Math.min(1, maxEdge / Math.max(srcW, srcH));
  const w = Math.max(1, Math.round(srcW * scale));
  const h = Math.max(1, Math.round(srcH * scale));

  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Canvas unsupported');

  ctx.drawImage(image, 0, 0, w, h);
  const imageData = ctx.getImageData(0, 0, w, h);
  const src = imageData.data;

  const blurR = style === 'comic' ? 1 : 2;
  const levels = style === 'comic' ? 5 : 7;
  const satBoost = style === 'comic' ? 1.35 : 1.2;
  const edgeThreshold = style === 'comic' ? 48 : 72;
  const inkStrength = style === 'comic' ? 0.92 : 0.55;

  const soft = boxBlur(src, w, h, blurR);
  const out = new Uint8ClampedArray(src.length);

  // Posterize + saturate into working buffer
  for (let i = 0; i < soft.length; i += 4) {
    const a = soft[i + 3];
    if (a < 8) {
      out[i] = 0;
      out[i + 1] = 0;
      out[i + 2] = 0;
      out[i + 3] = 0;
      continue;
    }

    let r = soft[i];
    let g = soft[i + 1];
    let b = soft[i + 2];

    // Saturation boost around luma
    const y = luminance(r, g, b);
    r = y + (r - y) * satBoost;
    g = y + (g - y) * satBoost;
    b = y + (b - y) * satBoost;

    out[i] = posterizeChannel(r, levels);
    out[i + 1] = posterizeChannel(g, levels);
    out[i + 2] = posterizeChannel(b, levels);
    out[i + 3] = a;
  }

  // Sobel edges on posterized luma → ink outlines
  const gray = new Float32Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      gray[y * w + x] =
        out[i + 3] < 8 ? -1 : luminance(out[i], out[i + 1], out[i + 2]);
    }
  }

  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const idx = y * w + x;
      if (gray[idx] < 0) continue;

      const tl = gray[(y - 1) * w + (x - 1)];
      const tc = gray[(y - 1) * w + x];
      const tr = gray[(y - 1) * w + (x + 1)];
      const ml = gray[y * w + (x - 1)];
      const mr = gray[y * w + (x + 1)];
      const bl = gray[(y + 1) * w + (x - 1)];
      const bc = gray[(y + 1) * w + x];
      const br = gray[(y + 1) * w + (x + 1)];

      // Skip if neighborhood has holes
      if (tl < 0 || tc < 0 || tr < 0 || ml < 0 || mr < 0 || bl < 0 || bc < 0 || br < 0) {
        continue;
      }

      const gx = -tl + tr - 2 * ml + 2 * mr - bl + br;
      const gy = -tl - 2 * tc - tr + bl + 2 * bc + br;
      const mag = Math.hypot(gx, gy);

      if (mag >= edgeThreshold) {
        const i = idx * 4;
        const t = Math.min(1, (mag - edgeThreshold) / 80) * inkStrength;
        out[i] = clampByte(out[i] * (1 - t));
        out[i + 1] = clampByte(out[i + 1] * (1 - t));
        out[i + 2] = clampByte(out[i + 2] * (1 - t));
      }
    }
  }

  imageData.data.set(out);
  ctx.putImageData(imageData, 0, 0);

  // If we downscaled for speed, scale back up to original for setSrc fidelity
  if (scale < 0.999) {
    const full = document.createElement('canvas');
    full.width = srcW;
    full.height = srcH;
    const fctx = full.getContext('2d');
    if (!fctx) return c.toDataURL('image/png');
    fctx.imageSmoothingEnabled = true;
    fctx.imageSmoothingQuality = 'high';
    fctx.drawImage(c, 0, 0, srcW, srcH);
    return full.toDataURL('image/png');
  }

  return c.toDataURL('image/png');
}

/**
 * Apply comic/cartoon stylization to the selected Fabric image only.
 */
export async function applyCartoonToImage(
  canvas: fabric.Canvas,
  image: fabric.Image,
  options: CartoonOptions = {}
): Promise<CartoonStyle> {
  const el = image.getElement() as HTMLImageElement;
  if (!el) throw new Error('No image element');

  const style = options.style || 'comic';
  const nextUrl = renderComicDataUrl(el, { ...options, style });

  await new Promise<void>((resolve, reject) => {
    image.setSrc(
      nextUrl,
      () => {
        try {
          // Stylization already baked in — drop adjustment filters so look stays
          image.filters = [];
          image.applyFilters();
          image.setCoords();
          canvas.requestRenderAll();
          resolve();
        } catch (err) {
          reject(err);
        }
      },
      { crossOrigin: 'anonymous' }
    );
  });

  return style;
}
